import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Plus, Trash2, Undo2 } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Select,
  StatusBadge,
  Textarea,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  useApplyPriceAdjustment,
  useCreatePriceAdjustmentPreview,
  useDeletePriceAdjustment,
  usePriceAdjustments,
  useRevertPriceAdjustment,
  type AdjustmentKind,
  type AdjustmentScopeType,
  type AdjustmentStatus,
  type PriceAdjustmentRow,
  type PriceAdjustmentSummary,
} from '../api/priceAdjustmentsApi.ts'

const KIND_LABELS: Record<AdjustmentKind, string> = {
  vpi: 'VPI-Lauf',
  manufacturer: 'Hersteller-Weitergabe',
  manual: 'Manuell',
}

const KIND_OPTIONS = (Object.keys(KIND_LABELS) as AdjustmentKind[]).map((k) => ({ value: k, label: KIND_LABELS[k] }))

const SCOPE_OPTIONS: { value: AdjustmentScopeType; label: string }[] = [
  { value: 'all', label: 'Alle aktiven Tarife' },
  { value: 'tariff', label: 'Bestimmter Tarif' },
  { value: 'product', label: 'Bestimmtes Produkt' },
  { value: 'tariff_component', label: 'Bestimmte Tarif-Komponente' },
]

function fmtPercent(factorBp: number): string {
  const pct = factorBp / 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(2).replace('.', ',')} %`
}

function fmtCents(cents: number, currency = 'EUR'): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency })
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export function PriceAdjustmentsSection() {
  const { toast } = useToast()
  const [statusFilter, setStatusFilter] = useState<AdjustmentStatus | 'all'>('all')
  const { data = [], isLoading, error, refetch } = usePriceAdjustments(
    statusFilter === 'all' ? undefined : statusFilter,
  )
  const apply = useApplyPriceAdjustment()
  const revert = useRevertPriceAdjustment()
  const del = useDeletePriceAdjustment()
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<PriceAdjustmentRow | null>(null)

  function summary(row: PriceAdjustmentRow): PriceAdjustmentSummary | null {
    return row.appliedSummary ?? row.previewSummary
  }

  const columns: ColumnDef<PriceAdjustmentRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Bezeichnung',
        cell: ({ row }) => (
          <div>
            <p className="text-small font-medium text-gray-900">{row.original.name}</p>
            <p className="text-caption text-gray-500">
              {KIND_LABELS[row.original.kind]} · {row.original.scopeType}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'factorBp',
        header: 'Faktor',
        size: 110,
        cell: ({ getValue }) => {
          const bp = getValue() as number
          const variant = bp > 2000 ? 'danger' : bp > 0 ? 'warning' : 'success'
          return <Badge variant={variant} size="sm">{fmtPercent(bp)}</Badge>
        },
      },
      {
        accessorKey: 'effectiveDate',
        header: 'Stichtag',
        size: 120,
        cell: ({ getValue }) => new Date(getValue() as string).toLocaleDateString('de-DE'),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ getValue }) => <StatusBadge status={`adjustment_${getValue() as string}`} />,
      },
      {
        id: 'impact',
        header: 'Auswirkung',
        size: 200,
        cell: ({ row }) => {
          const s = summary(row.original)
          if (!s) return <span className="text-caption text-gray-400">—</span>
          return (
            <div className="text-caption text-gray-700">
              <p>{s.affectedComponents} Komponenten · {s.affectedContracts} Verträge</p>
              <p className={s.totalRevenueChangeCents >= 0 ? 'text-green-700' : 'text-red-700'}>
                {s.totalRevenueChangeCents >= 0 ? '+' : ''}
                {fmtCents(s.totalRevenueChangeCents)} / Periode
              </p>
              {s.triggersSpecialTermination ? (
                <p className="inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="w-3 h-3" />
                  Sonderkündigungsrecht ausgelöst
                </p>
              ) : null}
            </div>
          )
        },
      },
      {
        id: '__actions__',
        header: '',
        enableSorting: false,
        size: 220,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            {row.original.status === 'preview' ? (
              <>
                <Button
                  variant="primary"
                  size="xs"
                  loading={apply.isPending}
                  onClick={(e) => {
                    e.stopPropagation()
                    apply.mutate(row.original.id, {
                      onSuccess: (r) =>
                        toast({
                          title: 'Anpassung angewendet',
                          description: r.applied.triggersSpecialTermination
                            ? 'Sonderkündigungsrecht wurde gesetzt.'
                            : undefined,
                          tone: 'success',
                        }),
                      onError: (err) =>
                        toast({
                          title: 'Anwenden fehlgeschlagen',
                          description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                          tone: 'danger',
                        }),
                    })
                  }}
                >
                  Anwenden
                </Button>
                <IconButton
                  icon={<Trash2 className="w-3.5 h-3.5" />}
                  aria-label="Verwerfen"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(row.original) }}
                />
              </>
            ) : null}
            {row.original.status === 'applied' ? (
              <Button
                variant="ghost"
                size="xs"
                leadingIcon={<Undo2 className="w-3.5 h-3.5" />}
                loading={revert.isPending}
                onClick={(e) => {
                  e.stopPropagation()
                  revert.mutate(row.original.id, {
                    onSuccess: (r) => toast({ title: `${r.reverted} Komponenten zurückgesetzt`, tone: 'success' }),
                    onError: (err) =>
                      toast({
                        title: 'Zurücknehmen fehlgeschlagen',
                        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                        tone: 'danger',
                      }),
                  })
                }}
              >
                Zurücknehmen
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [apply, revert, toast],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {(['all', 'preview', 'applied', 'reverted'] as const).map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'Alle' : s === 'preview' ? 'Entwürfe' : s === 'applied' ? 'Angewendet' : 'Zurückgenommen'}
            </Button>
          ))}
        </div>
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<Plus className="w-4 h-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Neue Preisanpassung
        </Button>
      </div>

      {isLoading ? <LoadingState label="Lade Preisanpassungen …" /> : null}
      {error ? (
        <ErrorState
          description="Preisanpassungen konnten nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
          onRetry={() => void refetch()}
        />
      ) : null}

      {!isLoading && !error ? (
        <DataTable<PriceAdjustmentRow>
          data={data}
          columns={columns}
          getRowId={(r) => r.id}
          searchPlaceholder="Anpassungen suchen …"
          emptyState={
            <EmptyState
              title="Noch keine Preisanpassung"
              description="Eine Preisanpassung wirkt auf Tarif-Komponenten und Mengenstaffeln. Bei Hersteller-Weitergaben über 20 % wird ein Sonderkündigungsrecht gesetzt."
              action={
                <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                  Neue Preisanpassung
                </Button>
              }
            />
          }
        />
      ) : null}

      <CreatePreviewModal open={createOpen} onOpenChange={setCreateOpen} />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Preisanpassung verwerfen?"
        message={confirmDelete ? `„${confirmDelete.name}" wird aus den Entwürfen gelöscht.` : ''}
        variant="danger"
        confirmLabel="Verwerfen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Anpassung verworfen', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) =>
              toast({
                title: 'Löschen fehlgeschlagen',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              }),
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}

// ─── Modal: neue Vorschau ───────────────────────────────────────────────────

function CreatePreviewModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { toast } = useToast()
  const create = useCreatePriceAdjustmentPreview()
  const [kind, setKind] = useState<AdjustmentKind>('vpi')
  const [name, setName] = useState('')
  const [factorPercent, setFactorPercent] = useState('2,5')
  const [effectiveDate, setEffectiveDate] = useState(todayIso())
  const [scopeType, setScopeType] = useState<AdjustmentScopeType>('all')
  const [scopeId, setScopeId] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [previewResult, setPreviewResult] = useState<PriceAdjustmentSummary | null>(null)

  useEffect(() => {
    if (open) {
      setKind('vpi')
      setName('')
      setFactorPercent('2,5')
      setEffectiveDate(todayIso())
      setScopeType('all')
      setScopeId('')
      setNotes('')
      setError(null)
      setPreviewResult(null)
    }
  }, [open])

  function parseFactorBp(): number | null {
    const trimmed = factorPercent.trim().replace(',', '.').replace('%', '').trim()
    const n = parseFloat(trimmed)
    if (!Number.isFinite(n)) return null
    return Math.round(n * 100)
  }

  function handlePreview() {
    setError(null)
    if (!name.trim()) {
      setError('Bezeichnung ist Pflicht.')
      return
    }
    const bp = parseFactorBp()
    if (bp == null) {
      setError('Faktor in Prozent ist ungültig.')
      return
    }
    if (bp === 0) {
      setError('Faktor darf nicht 0 sein.')
      return
    }
    if (scopeType !== 'all' && !scopeId.trim()) {
      setError('Für diesen Bereich ist eine Scope-ID erforderlich.')
      return
    }
    create.mutate(
      {
        kind,
        name: name.trim(),
        factorBp: bp,
        effectiveDate,
        scopeType,
        scopeId: scopeType === 'all' ? null : scopeId.trim(),
        notes: notes || null,
      },
      {
        onSuccess: (r) => {
          setPreviewResult(r.preview)
          toast({
            title: 'Vorschau erzeugt',
            description: `${r.preview.affectedComponents} Komponenten betroffen`,
            tone: 'success',
          })
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Vorschau fehlgeschlagen'),
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Neue Preisanpassung"
      description="Vorschau erzeugen und in der Liste prüfen, dann pro Eintrag Anwenden/Verwerfen."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Schließen
          </Button>
          <Button variant="primary" size="sm" onClick={handlePreview} loading={create.isPending}>
            Vorschau erzeugen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">{error}</div>
        ) : null}

        <Select<AdjustmentKind>
          label="Typ"
          value={kind}
          onValueChange={setKind}
          options={KIND_OPTIONS}
        />
        <Input
          label="Bezeichnung"
          hint={'z. B. „VPI 2026 +2,5 %" oder „Microsoft NCE Mai 2026 +5 %"'}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Faktor (in %)"
            hint="positiv = Erhöhung, negativ = Senkung. Mehr als 20 % bei Hersteller-Weitergabe löst Sonderkündigung aus."
            value={factorPercent}
            onChange={(e) => setFactorPercent(e.currentTarget.value)}
          />
          <Input
            label="Stichtag"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.currentTarget.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select<AdjustmentScopeType>
            label="Bereich"
            value={scopeType}
            onValueChange={setScopeType}
            options={SCOPE_OPTIONS}
          />
          {scopeType !== 'all' ? (
            <Input
              label="Scope-ID"
              hint="UUID des Tarifs/Produkts/Komponente"
              value={scopeId}
              onChange={(e) => setScopeId(e.currentTarget.value)}
            />
          ) : null}
        </div>
        <Textarea
          label="Notizen"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        {previewResult ? (
          <Card>
            <Card.Header>
              <Card.Title>Vorschau-Ergebnis</Card.Title>
            </Card.Header>
            <Card.Body className="space-y-2">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Komponenten" value={String(previewResult.affectedComponents)} />
                <Stat label="Verträge" value={String(previewResult.affectedContracts)} />
                <Stat
                  label="Revenue/Periode"
                  value={fmtCents(previewResult.totalRevenueChangeCents)}
                />
              </div>
              {previewResult.triggersSpecialTermination ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-small text-amber-800 inline-flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {previewResult.contractIdsTriggeringSpecialTermination.length} Verträge bekommen ein
                  Sonderkündigungsrecht (Faktor &gt; 20 % bei Hersteller-Weitergabe).
                </div>
              ) : null}
              <p className="text-caption text-gray-500">
                Die Vorschau wurde gespeichert und erscheint in der Liste mit Status „Entwurf".
                Anwenden setzt die neuen Preise auf alle Tarif-Komponenten und Mengenstaffeln.
              </p>
            </Card.Body>
          </Card>
        ) : null}
      </div>
    </Modal>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-caption text-gray-500">{label}</p>
      <p className="text-h4 text-gray-900">{value}</p>
    </div>
  )
}
